import React, { useState } from 'react';
import { 
  Users, 
  Award, 
  BookOpen, 
  Video, 
  Mail, 
  Phone, 
  MapPin,
  CheckCircle,
  GraduationCap,
  Globe,
  Star,
  BrainCircuit
} from 'lucide-react';

const AboutUs = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    mobile_number: '',
    email_address: '',
    syllabus: '',
    grade: ''
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errorMessage) setErrorMessage('');
  };

  const validateForm = () => {
    if (!formData.full_name.trim()) {
      setErrorMessage('Full name is required');
      return false;
    }
    if (!formData.mobile_number.trim()) {
      setErrorMessage('Mobile number is required');
      return false;
    }
    if (!/^\d{10,15}$/.test(formData.mobile_number.replace(/\D/g, ''))) {
      setErrorMessage('Mobile number must be 10-15 digits');
      return false;
    }
    if (!formData.email_address.trim()) {
      setErrorMessage('Email address is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address)) {
      setErrorMessage('Please enter a valid email address');
      return false;
    }
    if (!formData.syllabus) {
      setErrorMessage('Syllabus selection is required');
      return false;
    }
    if (!formData.grade) {
      setErrorMessage('Grade is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${apiBaseUrl}/api/book-demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          full_name: formData.full_name.trim(),
          mobile_number: formData.mobile_number.trim(),
          email_address: formData.email_address.trim(),
          syllabus: formData.syllabus,
          grade: parseInt(formData.grade)
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit registration');
      }

      const data = await response.json();
      console.log('Registration successful:', data);
      
      setIsSubmitted(true);
      setFormData({ full_name: '', mobile_number: '', email_address: '', syllabus: '', grade: '' });
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Registration error:', error);
      setErrorMessage(error.message || 'Failed to submit registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const syllabusOptions = ['CBSE', 'ICSE', 'IGSE', 'KERALA STATE'];
  const gradeOptions = [1,2,3,4,5,6,7,8, 9, 10, 11, 12];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-700 to-indigo-800 text-white py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-6xl">π</div>
          <div className="absolute bottom-10 right-20 text-8xl">∫</div>
          <div className="absolute top-1/2 left-1/3 text-7xl">∞</div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            {/* <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm"> */}
              <img src="./images-pub/nova-logo.png" alt="NOVA Logo" className="w-10 h-10 rounded-xl" />
              {/* </div> */}
            <h1 className="text-4xl md:text-5xl font-bold">About NOVA</h1>
          </div>
          <p className="text-center text-blue-100 max-w-2xl mx-auto text-lg">
            Transforming online education through personalized one-to-one tutoring - AI powered
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          
          {/* LEFT SIDE - About Us Content */}
          <div className="space-y-8">
            {/* Our Story */}
            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <BookOpen className="text-blue-600" size={24} />
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Our Story</h2>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Founded with a vision to make quality education accessible to every student, 
                NOVA has grown into a premier online tutoring platform. We believe that every 
                learner deserves personalized attention and guidance from expert tutors who 
                are passionate about teaching.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <span className="badge badge-primary badge-lg">2000+ Students</span>
                <span className="badge badge-secondary badge-lg">100+ Expert Tutors</span>
                <span className="badge badge-accent badge-lg">10+ Countries</span>
              </div>
            </div>

            {/* Our Mission */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Award className="text-indigo-600" size={24} />
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Our Mission</h2>
              </div>
              <p className="text-gray-700 leading-relaxed">
                To empower students with the knowledge and skills they need to excel academically, 
                while providing flexible, engaging, and effective online learning experiences that 
                fit their unique learning style and pace.
              </p>
            </div>

            {/* Why Choose Us */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Star className="text-yellow-500" size={24} />
                Why Choose NOVA?
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Users, text: "One-to-One Tuitions", color: "text-blue-600" },
                  { icon: GraduationCap, text: "Expert Qualified Tutors", color: "text-green-600" },
                  { icon: Video, text: "Interactive Live Classes", color: "text-purple-600" },
                  { icon: Globe, text: "Global Curriculum Support", color: "text-orange-600" },
                    { icon: BrainCircuit, text: "AI Integrated Learning", color: "text-orange-600" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <item.icon className={item.color} size={20} />
                    <span className="text-gray-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Qualification Requirements */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <CheckCircle className="text-green-500" size={22} />
                Our Tutors
              </h2>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-600">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Bachelor's or higher degree (B.Sc, M.Sc, B.Ed, Ph.D in Physics, Chemistry, Biology, or Maths)</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Strong academic record with proven subject expertise</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Proven teaching experience</span>
                </li>
                
                <li className="flex items-start gap-2 text-gray-600">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Passion for online education and mentorship</span>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="bg-gray-900 text-white rounded-2xl p-6">
              <h2 className="text-xl font-bold mb-4">Get in Touch</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Phone size={18} className="text-blue-400" />
                  <span>9400-483231</span>
                  <span className="mx-2">|</span>
                  <span>9747-654411</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={18} className="text-blue-400" />
                  <span>contact@novaonlinetuition.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin size={18} className="text-blue-400" />
                  <span>Online - Available Worldwide</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - Registration Form */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-5">
                <h2 className="text-2xl font-bold text-white text-center">
                  Demo Registration
                </h2>
                <p className="text-blue-100 text-center text-sm mt-1">
                  Book your free trial session today!
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                {/* Error Message Alert */}
                {errorMessage && (
                  <div className="alert alert-error shadow-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l-2-2m0 0l-2-2m2 2l2-2m-2 2l-2 2m0 0l2 2m-2-2l-2 2" /></svg>
                    <span>{errorMessage}</span>
                  </div>
                )}

                {/* Full Name Field */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium text-gray-700">Full Name *</span>
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className="input input-bordered w-full focus:input-primary"
                    required
                  />
                </div>

                {/* Mobile Number Field */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium text-gray-700">Mobile Number *</span>
                  </label>
                  <input
                    type="tel"
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={handleChange}
                    placeholder="Enter your mobile number"
                    maxLength="15"
                    className="input input-bordered w-full focus:input-primary"
                    required
                  />
                </div>

                {/* Email Address Field */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium text-gray-700">Email Address *</span>
                  </label>
                  <input
                    type="email"
                    name="email_address"
                    value={formData.email_address}
                    onChange={handleChange}
                    placeholder="Enter your email address"
                    className="input input-bordered w-full focus:input-primary"
                    required
                  />
                </div>

                {/* Grade Dropdown */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium text-gray-700">Grade *</span>
                  </label>
                  <select
                    name="grade"
                    value={formData.grade}
                    onChange={handleChange}
                    className="select select-bordered w-full"
                    required
                  >
                    <option value="" disabled>Select your grade</option>
                    {gradeOptions.map(grade => (
                      <option key={grade} value={grade}>Grade {grade}</option>
                    ))}
                  </select>
                </div>

                {/* Syllabus Dropdown */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium text-gray-700">Syllabus *</span>
                  </label>
                  <select
                    name="syllabus"
                    value={formData.syllabus}
                    onChange={handleChange}
                    className="select select-bordered w-full"
                    required
                  >
                    <option value="" disabled>Select your syllabus</option>
                    {syllabusOptions.map(syl => (
                      <option key={syl} value={syl}>{syl}</option>
                    ))}
                  </select>
                </div>

                {/* Submit Button */}
                <button 
                  type="submit" 
                  className="btn btn-primary w-full mt-4 gap-2"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Submitting...
                    </>
                  ) : (
                    'Register for Demo Class'
                  )}
                </button>

                {/* Success Message */}
                {isSubmitted && (
                  <div className="alert alert-success shadow-lg mt-4">
                    <CheckCircle size={20} />
                    <span>Registration successful! We'll contact you soon.</span>
                  </div>
                )}

               
              </form>
            </div>

            {/* Subjects Offered Card */}
            <div className="mt-6 bg-white rounded-xl p-4 shadow-md border border-gray-100">
              <h3 className="font-semibold text-gray-700 mb-2 text-center">📚 Subjects We Offer</h3>
              <div className="flex flex-wrap justify-center gap-2">
                <span className="badge badge-primary">Physics</span>
                <span className="badge badge-secondary">Chemistry</span>
                <span className="badge badge-accent">Biology</span>
                <span className="badge badge-info">Mathematics</span>
                <span className="badge badge-warning">All Subjects</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;